import React, {useEffect, useReducer} from 'react';
import {Helmet} from 'react-helmet';

import DashboardHeader from '../../components/DashboardHeader';
import Wrapper from '../../components/Wrapper';
import {useTranslation} from '../../hooks/useTranslation';

import './index.scss';
import {AddField, Button, FieldPicker, FormName} from './ui';
import {useRenderFields} from './model';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {
  useCreateCustomCategoryMutation,
  useLazyGetCustomFormDetailsQuery,
  useUpdateCustomCategoryMutation,
} from '../../store/builder/inspections.api';
import {useDispatch, useSelector} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {toast} from 'react-toastify';
import {setAllData, setFields, setMainLabel} from './service/createForm.slice';
import {INPUT_FIELD} from './fields/staticData';

const BuildInspection = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const userType = location?.state?.userType;
  const renderFields = useRenderFields();
  const category = useSelector(state => state.rootReducer.createFormSlice.data);
  let isReadOnly = Boolean(location.search.split('=')[1]);

  const [
    createCategoryForm,
    {
      data: createCategoryFormData,
      isError: createCategoryFormIsError,
      isLoading: createCategoryFormIsLoading,
      error: createCategoryFormError,
      isSuccess: createCategoryFormIsSuccess,
    },
  ] = useCreateCustomCategoryMutation();

  const [
    updateCategoryForm,
    {
      data: updateCategoryFormData,
      isError: updateCategoryFormIsError,
      isLoading: updateCategoryFormIsLoading,
      error: updateCategoryFormError,
      isSuccess: updateCategoryFormIsSuccess,
    },
  ] = useUpdateCustomCategoryMutation();

  const [
    getCategoryDetails,
    {
      data: getCategoryDetailsData,
      isError: getCategoryDetailsIsError,
      isLoading: getCategoryDetailsIsLoading,
      error: getCategoryDetailsError,
      isSuccess: getCategoryDetailsIsSuccess,
    },
  ] = useLazyGetCustomFormDetailsQuery();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    createCategoryFormError,
    getCategoryDetailsError,
    updateCategoryFormError,
  ]);

  useEffect(() => {
    if (params?.id !== undefined) {
      getCategoryDetails(params.id);
    }
  }, []);

  useEffect(() => {
    if (params?.id && getCategoryDetailsData?.data) {
      dispatch(
        setAllData({
          ...getCategoryDetailsData.data,
          fields: getCategoryDetailsData?.data?.fields?.map(item => ({
            ...item,
            field_type: item.type,
          })),
        }),
      );
    }
  }, [getCategoryDetailsData]);

  useEffect(() => {
    dispatch(
      setLoader(
        createCategoryFormIsLoading ||
          getCategoryDetailsIsLoading ||
          updateCategoryFormIsLoading,
      ),
    );
  }, [
    createCategoryFormIsLoading,
    getCategoryDetailsIsLoading,
    updateCategoryFormIsLoading,
  ]);

  useEffect(() => {
    if (updateCategoryFormIsSuccess) {
      if (userType === 'corporate') {
        navigate('/corporate/forms');
      } else {
        navigate('/forms');
      }
      dispatch(setLoader(false));
      toast.success(t('createNewForm.messages.formUpdatedSuccess'));
    }

    if (createCategoryFormIsSuccess) {
      navigate(-1);
      dispatch(setLoader(false));
      toast.success(t('createNewForm.messages.formCreatedSuccess'));
    }
  }, [updateCategoryFormIsSuccess, createCategoryFormIsSuccess]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCreate = () => {
    if (params?.id !== undefined) {
      updateCategoryForm({
        body: {...category, corporate: userType === 'corporate'},
        id: params.id,
      });
    } else {
      createCategoryForm({...category, corporate: userType === 'corporate'});
    }
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('createNewForm.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={params?.id ? t('createNewForm.editForm') : t('createNewForm.createForm')}
        type='build-form'
        action={handleCancel}
      />
      <div className='f-build'>
        <div className='f-build__container'>
          <div className='f-build__content'>
            <FormName category={category} readOnly={isReadOnly} />
            <div className='f-build__fields'>
              {renderFields.render()}
              {!isReadOnly && <AddField />}
            </div>
            <div className='f-build__buttons'>
              <Button text={t('createNewForm.cancel')} type={'light'} onClick={handleCancel} />
              {!isReadOnly && (
                <Button
                  text={params?.id ? t('createNewForm.update') : t('createNewForm.create')}
                  onClick={handleCreate}
                />
              )}
            </div>
          </div>
          {!isReadOnly && <FieldPicker />}
        </div>
      </div>
    </Wrapper>
  );
};

export default BuildInspection;
